<!-- 
Dialog for uploading a CSV file.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog sm">
    <v-card>
      <v-toolbar color="secondary" flat dark>
        <v-toolbar-title>
          {{ description }}
        </v-toolbar-title>
        <v-spacer/>
        <our-user-guide-button topic="levy-collection-setup" section="manual-uploads"/>
      </v-toolbar>
      <our-busy-indicator :show="state.processing"/>
      <v-card-text>
        <v-form v-if="!state.error.show" ref="form" enctype="multipart/form-data" novalidate>
          <div :class="state.processing || !state.isDryRun ? '' : 'dropbox-active'" class="dropbox">
            <input 
              v-if="!state.processing"
              type="file"
              class="input-file" 
              @change="filesChange($event.target.files)"
            >
            <p v-if="!state.processing && state.isDryRun" class="subheading ml-4 mr-4">
              Drag the file here to begin<br>or click to browse.
              <br><br>
              <span class="caption">
                Use the "Download a template" button from below to get started 
                <br>
                with authoring a CSV file in the correct format.
                <br>
                The CSV file will be pre-populated with any existing data.
              </span>
            </p>
            <template v-if="!state.processing && !state.isDryRun">
              <p v-if="state.data.added || state.data.updated || state.data.removed">
                If you proceed:
                <br>
                <span v-if="state.data.added">{{ pluralise(state.data.added) }} will be added</span>
                <br>
                <span v-if="state.data.updated">{{ pluralise(state.data.updated) }} will be updated</span>
                <br>
                <span v-if="state.data.removed">{{ pluralise(state.data.removed) }} will be removed</span>
                <br>
                <br>
                Click on the upload button to 
                proceed with the upload.
              </p>
              <p v-else>
                Your spreadsheet matches the data already in the system.
                <br>
                <br>
                Click on the upload button if you want to proceed anyhow.
              </p>
            </template>
            <p v-if="state.processing && state.isDryRun">
              Validating the spreadsheet, please wait...
            </p>
            <p v-if="state.processing && !state.isDryRun">
              Uploading the spreadsheet, please wait...
            </p>
          </div>
        </v-form>
        <spreadsheet-upload-error-panel :error="state.error"/>
        <v-checkbox 
          v-if="!state.processing && state.isDryRun && !state.error.show && showPreviousYearsDataCheckbox" 
          v-model="usePreviousYearsData"
          label="Include data from previous years" 
          dense hide-details 
          color="primary" 
          class="font-weight-regular"
        />
      </v-card-text>
      <v-card-actions>
        <v-btn v-if="state.error.show" flat @click="reset">Try again</v-btn>
        <v-btn v-if="!state.processing && state.isDryRun && !state.error.show" flat @click="getTemplate">
          Download a template
        </v-btn>
        <v-spacer/>
        <v-btn :disabled="state.processing" flat @click="hide">Cancel</v-btn>
        <v-btn 
          :disabled="state.processing || state.isDryRun"
          flat color="primary" @click="save"
        >
          Upload
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import SpreadsheetUploadErrorPanel from "@/components/levy-collection-providers/SpreadsheetUploadErrorPanel.vue";
import * as mutation from "@/store/levy-collection-providers/mutation-types";
import * as action from "@/store/levy-collection-providers/action-types";

export default {
  components: { SpreadsheetUploadErrorPanel },
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    description: {
      type: String,
      required: true
    },
    endpointSuffix: {
      type: String,
      required: true
    },
    levyCollectionId: {
      type: Number,
      required: true
    },
    showPreviousYearsDataCheckbox: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      uploadedFiles: [],
      formData: null,
      usePreviousYearsData: false,
      showValidationDialog: false,
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollectionProviders.providerUpload;
    }
  },
  watch: {
    show() {
      this.reset();
    }
  },
  methods: {
    hide() {
      this.$emit("update:show", false);
    },
    pluralise(count) {
      if (count == 0) {
        return `No providers`;
      } else {
        return `${count} ${count > 1 ? "providers" : "provider"}`;
      }
    },
    reset() {
      this.$store.commit(mutation.UPLOAD_SPREADSHEET_RESET, this.levyCollectionId);
      this.uploadedFiles = [];
      this.formData = null;
      this.usePreviousYearsData = false;
    },
    async save() {
      if (this.$refs.form.validate() && await this.$store.dispatch(action.UPLOAD_SPREADSHEET, {
        formData: this.formData,
        endpointSuffix: this.endpointSuffix
      }))
      {
        if (this.state.validated) {
          if (!this.state.isDryRun) {
            this.hide();
          } else {
            this.$store.commit(mutation.UPLOAD_SPREADSHEET_SET_DRY_RUN, false);
          }
        }
      }
      else {
        this.showValidationDialog = true;
      }
    },
    filesChange(fileList) {
      if (fileList.length) {
        const filename = fileList[0].name;
        if (filename) {
          this.formData = new FormData();
          this.formData.append("spreadsheet", fileList[0]);
          this.save();
        }
      }
    },
    async getTemplate() {
      await this.$store.dispatch(action.UPLOAD_GET_TEMPLATE, {
        levyCollectionId: this.levyCollectionId,
        endpointSuffix: this.endpointSuffix,
        usePrevious: this.usePreviousYearsData
      });
    }
  }
};
</script>

<style lang="scss">
.dropbox-active {
  outline: 2px dashed grey; /* the dash box */
  .input-file {
    cursor: pointer;
  }
}

.dropbox-active:hover {
  background: lightgray; /* when mouse over to the drop zone, change color */
}

.dropbox {
  outline-offset: -10px;
  color: dimgray;
  padding: 10px 10px;
  min-height: 300px; /* minimum height */
  position: relative;
  cursor: pointer;
}

.input-file {
  opacity: 0; /* invisible but it's there! */
  width: 100%;
  height: 300px;
  position: absolute;
}

.dropbox p {
  font-size: 1.2em;
  text-align: center;
  padding: 50px 0;
}
</style>